

import { useState, useEffect, useMemo, useCallback } from "react";
import { localToday } from "@/lib/data";
import Modal from "../../components/Modal";
import { eventsApi, clientsApi, mastersApi, vendorsApi } from "@/lib/api";
import { toUnit, unitSuffix, lineQuantity } from "@/lib/pricing";
import { useToast } from "@/context/ToastContext";
import { Plus, Trash2, X, CheckCircle2, XCircle, AlertTriangle, Check } from "lucide-react";

// ─── Types ──────────────────────────────────────────────────────────────────

interface Client   { id: number; name: string }
interface Package {
  id: number; name: string; is_active?: boolean;
  inclusions?: string[];
  min_guests?: number | null; max_guests?: number | null;
  event_types?: string[];
  discount_type?: "percent" | "fixed" | null; discount_value?: number;
  services_data?: PackageSvc[];
}
/** One package line, as GET /masters/packages returns it */
interface PackageSvc {
  line_id?: number;
  service_id: number; name: string; category?: string | null;
  unit?: string; is_optional?: boolean;
  unit_price: number; cost_price: number;
  vendor_id?: number | null; vendor_name?: string | null; quantity?: number;
}
interface Venue   { id: number; name: string }
interface Vendor  { id: number; name: string }

interface ServiceRow {
  service_id?: number | null;
  vendor_id?: number | null;
  description: string;
  quantity: number;
  unit_price: number;
  cost_price: number;
  // ── Local only (not sent) ──
  vendor_name?: string | null;
  /** Quantity follows the guest count: quantity = qty_per_guest × guests */
  per_guest?: boolean;
  qty_per_guest?: number;
  /** Id of the package this row came from while it is still untouched */
  from_pkg?: number;
}

interface VendorRow {
  vendor_id: number | null;
  description: string;
  quantity: number;
  unit_price: number;
  cost_price: number;
  commission_percent: number;
  commission_type: "percentage" | "fixed";
  notes: string;
}

interface VenueStatus {
  id: number;
  status: "available" | "occupied";
  conflict: { event_name: string; client_name: string | null } | null;
}

interface AddEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventAdded: () => void;
  defaultVenueId?: number;
  defaultStartDate?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const inputCls = (err?: string) =>
  `w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${
    err
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
  }`;

const fmt = (n: number) =>
  "₹" + Math.round(n).toLocaleString("en-IN");

const guestRange = (min?: number | null, max?: number | null) =>
  min && max ? `${min}–${max}` : min ? `${min}+` : `up to ${max}`;

/** A service row from a package line — keeps the line's vendor; per-guest lines follow the guest count */
const rowFromPackage = (l: PackageSvc, guests: number, pkgId?: number): ServiceRow => {
  const perGuest = toUnit(l.unit) === "guest";
  return {
    service_id:    l.service_id,
    vendor_id:     l.vendor_id ?? null,
    vendor_name:   l.vendor_name ?? null,
    description:   l.name,
    quantity:      lineQuantity(l, guests),
    unit_price:    Number(l.unit_price) || 0,
    cost_price:    Number(l.cost_price) || 0,
    per_guest:     perGuest,
    qty_per_guest: perGuest ? Math.max(1, Number(l.quantity) || 1) : undefined,
    from_pkg:      pkgId,
  };
};

/** Rows the user added or changed (blank rows don't count) */
const isOwnRow = (r: ServiceRow) => !r.from_pkg && (!!r.description.trim() || !!r.service_id);

// ─── Blank rows ──────────────────────────────────────────────────────────────

const blankService = (): ServiceRow => ({
  service_id: null, description: "", quantity: 1, unit_price: 0, cost_price: 0,
});

const blankVendor = (): VendorRow => ({
  vendor_id: null, description: "", quantity: 1,
  unit_price: 0, cost_price: 0,
  commission_percent: 0, commission_type: "percentage", notes: "",
});

// ─── Component ───────────────────────────────────────────────────────────────

export default function AddEventModal({ isOpen, onClose, onEventAdded, defaultVenueId, defaultStartDate }: AddEventModalProps) {
  const { showToast } = useToast();
  const [loading, setLoading]   = useState(false);

  // Drop-down data
  const [clients,    setClients]    = useState<Client[]>([]);
  const [packages,   setPackages]   = useState<Package[]>([]);
  const [venues,     setVenues]     = useState<Venue[]>([]);
  const [vendors,    setVendors]    = useState<Vendor[]>([]);
  const [eventTypes, setEventTypes] = useState<string[]>([]);

  // Basic info
  const [clientId,    setClientId]    = useState("");
  const [eventType,   setEventType]   = useState("");
  const [startDate,   setStartDate]   = useState("");
  const [guestCount,  setGuestCount]  = useState("");
  const [packageId,   setPackageId]   = useState("");
  const [venueId,     setVenueId]     = useState("");
  const [status,      setStatus]      = useState("confirmed");
  // Until the user picks a status, it follows the date: past events "completed", upcoming ones "confirmed"
  const [statusTouched, setStatusTouched] = useState(false);
  const [notes,       setNotes]       = useState("");

  // Venue availability hints
  const [venueStatuses,     setVenueStatuses]     = useState<VenueStatus[]>([]);
  const [checkingAvail,     setCheckingAvail]     = useState(false);

  // Services & vendors
  const [services,    setServices]    = useState<ServiceRow[]>([]);
  const [eventVendors,setEventVendors]= useState<VendorRow[]>([]);

  // A package picked while the user already has their own rows — waits for "Replace / Add below / Cancel"
  const [pendingPkg,  setPendingPkg]  = useState<Package | null>(null);

  // Discount (a package's discount fills it while it is empty)
  const [discountType,  setDiscountType]  = useState<"fixed" | "percentage">("fixed");
  const [discountValue, setDiscountValue] = useState("");
  // The discount copied from a package — replaced by the next package unless the user changed it
  const [pkgDiscount,   setPkgDiscount]   = useState<{ type: "fixed" | "percentage"; value: string; from: string } | null>(null);

  // Payment
  const [advancePayment, setAdvancePayment] = useState("");
  const [paymentMethod,  setPaymentMethod]  = useState("cash");
  const [paymentDate,    setPaymentDate]    = useState("");

  // Inline "add client"
  const [showClientForm,  setShowClientForm]  = useState(false);
  const [creatingClient,  setCreatingClient]  = useState(false);
  const [newClient,       setNewClient]       = useState({ name: "", email: "", phone: "", address: "" });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── Load drop-downs on open ──────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
    loadData();
  }, [isOpen]);

  // Apply defaults from calendar/availability checker
  useEffect(() => {
    if (!isOpen) return;
    if (defaultVenueId) setVenueId(String(defaultVenueId));
    if (defaultStartDate) {
      setStartDate(defaultStartDate);
      setStatus(defaultStartDate < localToday() ? "completed" : "confirmed");
    }
  }, [isOpen, defaultVenueId, defaultStartDate]);

  // Auto-check venue availability when startDate changes
  const checkVenueAvailability = useCallback(async (date: string) => {
    if (!date) { setVenueStatuses([]); return; }
    setCheckingAvail(true);
    try {
      const res = await mastersApi.venues.checkAllAvailability(date, date);
      const data = res.data?.data ?? res.data;
      setVenueStatuses(data?.venues ?? []);
    } catch {
      setVenueStatuses([]);
    } finally {
      setCheckingAvail(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !startDate) return;
    const timer = setTimeout(() => checkVenueAvailability(startDate), 400);
    return () => clearTimeout(timer);
  }, [startDate, isOpen, checkVenueAvailability]);

  async function loadData() {
    try {
      const [cRes, pRes, venuRes, etRes, venRes] = await Promise.all([
        clientsApi.list(),
        mastersApi.packages.list({ active_only: 1 }),
        mastersApi.venues.list(),
        mastersApi.eventTypes.list({ status: "active" }),
        vendorsApi.list("for_select=1"),
      ]);
      setClients(cRes.data?.data    || cRes.data    || []);
      setPackages(pRes.data?.data || pRes.data || []);
      setVenues(venuRes.data?.data  || venuRes.data || []);
      setVendors(venRes.data?.data  || venRes.data  || []);
      const et = etRes.data?.data   || etRes.data   || [];
      setEventTypes(et.map((t: any) => t.name));
    } catch {
      showToast("Failed to load form data", "error");
    }
  }

  // ── Package → service rows ──────────────────────────────────────────────

  const discountIsFromPackage = !!pkgDiscount && pkgDiscount.type === discountType && pkgDiscount.value === discountValue;

  function applyPackage(pkg: Package, mode: "replace" | "append") {
    setPendingPkg(null);
    setPackageId(String(pkg.id));

    const guests = parseInt(guestCount) || 0;
    const rows = (pkg.services_data || []).filter((l) => !l.is_optional).map((l) => rowFromPackage(l, guests, pkg.id));
    // A package without lines only sets the package; the rows stay as they are
    if (rows.length) setServices((prev) => (mode === "append" ? [...prev, ...rows] : rows));

    // Package discount → event discount, only while the form's discount is empty (or still the last package's)
    const ours = !((parseFloat(discountValue) || 0) > 0) || discountIsFromPackage;
    if (!ours) return;
    if (pkg.discount_type && Number(pkg.discount_value) > 0) {
      const type = pkg.discount_type === "percent" ? "percentage" : "fixed";
      const value = String(Number(pkg.discount_value));
      setDiscountType(type);
      setDiscountValue(value);
      setPkgDiscount({ type, value, from: pkg.name });
    } else if (discountIsFromPackage) {
      setDiscountValue("");
      setPkgDiscount(null);
    }
  }

  function handlePackageSelect(value: string) {
    if (!value) {
      // Keep the rows — the user manages them by hand once the package is removed
      setPackageId("");
      setPendingPkg(null);
      if (discountIsFromPackage) { setDiscountValue(""); setPkgDiscount(null); }
      return;
    }
    const pkg = packages.find((p) => p.id === parseInt(value));
    if (!pkg) { setPackageId(value); return; }
    const addsRows = (pkg.services_data || []).some((l) => !l.is_optional);
    // Never drop the user's own rows without asking
    if (addsRows && services.some(isOwnRow)) { setPendingPkg(pkg); return; }
    applyPackage(pkg, "replace");
  }

  function addPackageAddOn(l: PackageSvc) {
    // Picked by the user, so it is their row (no from_pkg)
    setServices((prev) => [...prev, rowFromPackage(l, parseInt(guestCount) || 0)]);
  }

  // Per-guest rows follow the guest count
  function handleGuestCountChange(value: string) {
    setGuestCount(value);
    setErrors((p) => ({ ...p, guestCount: "" }));
    const guests = parseInt(value) || 0;
    if (guests > 0) {
      setServices((prev) => prev.map((r) => (r.per_guest ? { ...r, quantity: (r.qty_per_guest || 1) * guests } : r)));
    }
  }

  const selectedPkg = packages.find((p) => String(p.id) === packageId);
  const guestsNum   = parseInt(guestCount) || 0;

  const addOns = (selectedPkg?.services_data || []).filter(
    (l) => l.is_optional && !services.some((r) => r.service_id === l.service_id)
  );

  const packageHints: string[] = [];
  if (selectedPkg) {
    const { name, min_guests: min, max_guests: max, event_types: types } = selectedPkg;
    if (guestsNum > 0 && ((min && guestsNum < min) || (max && guestsNum > max))) {
      packageHints.push(`${name} is for ${guestRange(min, max)} guests`);
    }
    if (types?.length && eventType && !types.some((t) => t.toLowerCase() === eventType.toLowerCase())) {
      packageHints.push(`${name} is meant for ${types.join(" / ")} events`);
    }
  }

  // ── Financials ───────────────────────────────────────────────────────────

  const totals = useMemo(() => {
    const svcRevenue  = services.reduce((s, r) => s + r.unit_price * r.quantity, 0);
    const svcCost     = services.reduce((s, r) => s + r.cost_price * r.quantity, 0);
    const vndRevenue  = eventVendors.reduce((s, r) => s + r.unit_price * r.quantity, 0);
    const vndCost     = eventVendors.reduce((s, r) => s + r.cost_price * r.quantity, 0);
    const subtotal    = svcRevenue + vndRevenue;
    const dv          = parseFloat(discountValue) || 0;
    const discount    = discountType === "percentage" ? (subtotal * Math.min(dv, 100)) / 100 : Math.min(dv, subtotal);
    const total       = subtotal - discount;
    const paid        = parseFloat(advancePayment) || 0;
    const balance     = total - paid;
    return { svcRevenue, svcCost, vndRevenue, vndCost, subtotal, discount, total, paid, balance };
  }, [services, eventVendors, advancePayment, discountType, discountValue]);

  // ── Validation ───────────────────────────────────────────────────────────

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!clientId)    e.clientId   = "Client is required";
    if (!eventType)   e.eventType  = "Event type is required";
    if (!startDate)   e.startDate  = "Event date is required";
    if (!guestCount || parseInt(guestCount) < 1) e.guestCount = "Valid guest count required";
    if ((parseFloat(discountValue) || 0) < 0) e.discount = "Invalid discount";
    if (discountType === "percentage" && (parseFloat(discountValue) || 0) > 100) e.discount = "Max 100%";

    // Validate service rows
    services.forEach((s, i) => {
      if (!s.description.trim()) e[`svc_desc_${i}`]  = "Required";
      if (s.unit_price < 0)      e[`svc_price_${i}`] = "Invalid";
    });

    // Validate vendor rows
    eventVendors.forEach((v, i) => {
      if (!v.vendor_id)           e[`vnd_id_${i}`]    = "Select vendor";
      if (!v.description.trim())  e[`vnd_desc_${i}`]  = "Required";
      if (v.unit_price < 0)       e[`vnd_price_${i}`] = "Invalid";
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // ── Submit ───────────────────────────────────────────────────────────────

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const client    = clients.find((c) => c.id === parseInt(clientId));
      const eventName = `${client?.name || "Event"} - ${eventType}`;

      await eventsApi.createPastEvent({
        client_id:       parseInt(clientId),
        name:            eventName,
        event_type:      eventType,
        start_date:      startDate,
        guest_count:     parseInt(guestCount),
        package_id:      packageId ? parseInt(packageId) : null,
        venue_id:        venueId   ? parseInt(venueId)   : null,
        status,
        notes:           notes || null,
        discount_type:   discountType,
        discount_amount: parseFloat(discountValue) || 0,
        // Services (vendor kept from the package line)
        services_data: services.map((s) => ({
          service_id:  s.service_id ?? null,
          vendor_id:   s.vendor_id ?? null,
          description: s.description,
          quantity:    s.quantity,
          unit_price:  s.unit_price,
          cost_price:  s.cost_price,
        })),
        // Vendors
        vendors_data: eventVendors.map((v) => ({
          vendor_id:          v.vendor_id,
          description:        v.description,
          quantity:           v.quantity,
          unit_price:         v.unit_price,
          cost_price:         v.cost_price,
          commission_percent: v.commission_percent,
          commission_type:    v.commission_type,
          notes:              v.notes || null,
        })),
        // Payment
        advance_payment: parseFloat(advancePayment) || 0,
        payment_method:  paymentMethod,
        payment_date:    paymentDate || startDate,
      });

      showToast("Event created successfully", "success");
      onEventAdded();
      onClose();
    } catch (err: any) {
      showToast(err?.message || "Failed to create event", "error");
    } finally {
      setLoading(false);
    }
  }

  // ── Reset ────────────────────────────────────────────────────────────────

  function resetForm() {
    setClientId(""); setEventType(""); setStartDate("");
    setGuestCount(""); setPackageId(""); setVenueId("");
    setStatus("confirmed"); setStatusTouched(false); setNotes("");
    setServices([]); setEventVendors([]);
    setPendingPkg(null);
    setDiscountType("fixed"); setDiscountValue(""); setPkgDiscount(null);
    setAdvancePayment(""); setPaymentMethod("cash"); setPaymentDate("");
    setShowClientForm(false);
    setNewClient({ name: "", email: "", phone: "", address: "" });
    setErrors({});
    setVenueStatuses([]);
  }

  // ── Inline client creation ────────────────────────────────────────────────

  async function handleCreateClient() {
    if (!newClient.name.trim()) { showToast("Client name required", "error"); return; }
    setCreatingClient(true);
    try {
      const res = await clientsApi.create(newClient);
      const c   = res.data?.data || res.data;
      setClients((prev) => [...prev, c]);
      setClientId(String(c.id));
      setNewClient({ name: "", email: "", phone: "", address: "" });
      setShowClientForm(false);
      showToast("Client created", "success");
    } catch {
      showToast("Failed to create client", "error");
    } finally {
      setCreatingClient(false);
    }
  }

  // ── Service row helpers ───────────────────────────────────────────────────

  function updateService(i: number, field: keyof ServiceRow, val: any) {
    setServices((prev) => prev.map((r, idx) => idx === i
      ? {
          ...r,
          [field]: val,
          from_pkg: undefined, // edited → the user's row now
          // A hand-typed quantity stops following the guest count
          ...(field === "quantity" ? { per_guest: false } : {}),
        }
      : r));
  }

  function removeService(i: number) {
    setServices((prev) => prev.filter((_, idx) => idx !== i));
  }

  // ── Vendor row helpers ────────────────────────────────────────────────────

  function updateVendor(i: number, field: keyof VendorRow, val: any) {
    setEventVendors((prev) => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  }

  function removeVendor(i: number) {
    setEventVendors((prev) => prev.filter((_, idx) => idx !== i));
  }

  // ─────────────────────────────────────────────────────────────────────────

  const footer = (
    <div className="flex gap-3">
      <button
        type="button"
        onClick={onClose}
        disabled={loading}
        className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        form="past-event-form"
        type="submit"
        disabled={loading}
        className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? (
          <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Creating...</>
        ) : (
          <><Plus className="w-4 h-4" />Create Event</>
        )}
      </button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New event" size="xl" footer={footer}>
      <form id="past-event-form" onSubmit={handleSubmit} className="space-y-6">

        {/* ── Section 1: Basic Info ─────────────────────────────────────── */}
        <section>
          <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Basic Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Client */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-stone-600 mb-1">Client *</label>
              <div className="flex gap-2">
                <select
                  value={clientId}
                  onChange={(e) => { setClientId(e.target.value); setErrors((p) => ({ ...p, clientId: "" })); }}
                  className={`${inputCls(errors.clientId)} flex-1`}
                >
                  <option value="">Select client</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => setShowClientForm((v) => !v)}
                  className="px-3 py-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                  title="Add new client"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {errors.clientId && <p className="text-xs text-rose-500 mt-1">{errors.clientId}</p>}

              {/* Inline add-client form */}
              {showClientForm && (
                <div className="mt-3 bg-stone-50 border border-stone-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-stone-700">New Client</span>
                    <button type="button" onClick={() => setShowClientForm(false)}>
                      <X className="w-4 h-4 text-stone-400 hover:text-stone-600" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <input
                        placeholder="Client name *"
                        value={newClient.name}
                        onChange={(e) => setNewClient((p) => ({ ...p, name: e.target.value }))}
                        className={inputCls()}
                      />
                    </div>
                    <input
                      placeholder="Email"
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient((p) => ({ ...p, email: e.target.value }))}
                      className={inputCls()}
                    />
                    <input
                      placeholder="Phone"
                      value={newClient.phone}
                      onChange={(e) => setNewClient((p) => ({ ...p, phone: e.target.value }))}
                      className={inputCls()}
                    />
                    <div className="col-span-2">
                      <input
                        placeholder="Address"
                        value={newClient.address}
                        onChange={(e) => setNewClient((p) => ({ ...p, address: e.target.value }))}
                        className={inputCls()}
                      />
                    </div>
                    <div className="col-span-2">
                      <button
                        type="button"
                        disabled={creatingClient || !newClient.name.trim()}
                        onClick={handleCreateClient}
                        className="w-full py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {creatingClient
                          ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Creating...</>
                          : <><Plus className="w-4 h-4" />Create Client</>}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Event Type */}
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Event Type *</label>
              <select
                value={eventType}
                onChange={(e) => { setEventType(e.target.value); setErrors((p) => ({ ...p, eventType: "" })); }}
                className={inputCls(errors.eventType)}
              >
                <option value="">Select type</option>
                {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {errors.eventType && <p className="text-xs text-rose-500 mt-1">{errors.eventType}</p>}
            </div>

            {/* Date */}
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Event Date *</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  const value = e.target.value;
                  setStartDate(value);
                  setErrors((p) => ({ ...p, startDate: "" }));
                  if (!statusTouched && value) setStatus(value < localToday() ? "completed" : "confirmed");
                }}
                className={inputCls(errors.startDate)}
              />
              {errors.startDate && <p className="text-xs text-rose-500 mt-1">{errors.startDate}</p>}
            </div>

            {/* Guest Count */}
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">No. of Guests *</label>
              <input
                type="number"
                min="1"
                placeholder="e.g. 200"
                value={guestCount}
                onChange={(e) => handleGuestCountChange(e.target.value)}
                className={inputCls(errors.guestCount)}
              />
              {errors.guestCount && <p className="text-xs text-rose-500 mt-1">{errors.guestCount}</p>}
            </div>

            {/* Package */}
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Package (optional)</label>
              <select
                value={packageId}
                onChange={(e) => handlePackageSelect(e.target.value)}
                className={inputCls()}
              >
                <option value="">No package</option>
                {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {selectedPkg?.services_data?.some((l) => !l.is_optional) && !pendingPkg && (
                <p className="text-xs text-amber-600 mt-1">Services loaded from package below</p>
              )}
            </div>

            {/* Package: confirm / hints / what's included */}
            {pendingPkg ? (
              <div className="sm:col-span-2 rounded-xl border border-amber-300 bg-amber-50 p-3">
                <p className="text-sm font-semibold text-stone-800">Use “{pendingPkg.name}”?</p>
                <p className="text-xs text-stone-600 mt-0.5">
                  You already have {services.filter(isOwnRow).length} service row{services.filter(isOwnRow).length === 1 ? "" : "s"} of your own.
                  Replace them with the package, or keep them and add the package below?
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => applyPackage(pendingPkg, "replace")}
                    className="flex-1 min-w-[140px] min-h-[40px] px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors"
                  >
                    Replace lines
                  </button>
                  <button
                    type="button"
                    onClick={() => applyPackage(pendingPkg, "append")}
                    className="flex-1 min-w-[140px] min-h-[40px] px-3 py-2 rounded-lg bg-white border border-amber-300 text-amber-800 text-sm font-semibold hover:bg-amber-100 transition-colors"
                  >
                    Add package lines below mine
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingPkg(null)}
                    className="flex-1 min-w-[100px] min-h-[40px] px-3 py-2 rounded-lg bg-white border border-stone-200 text-stone-600 text-sm font-semibold hover:bg-stone-100 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : selectedPkg && (packageHints.length > 0 || !!selectedPkg.inclusions?.length) ? (
              <div className="sm:col-span-2 space-y-2">
                {packageHints.map((hint) => (
                  <p key={hint} className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                    <AlertTriangle className="w-3.5 h-3.5 mt-px flex-shrink-0" />
                    {hint}
                  </p>
                ))}
                {!!selectedPkg.inclusions?.length && (
                  <div className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-2.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500 mb-1.5">Included</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                      {selectedPkg.inclusions.map((item, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-xs text-stone-700">
                          <Check className="w-3.5 h-3.5 mt-px text-emerald-600 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : null}

            {/* Venue */}
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1 flex items-center gap-1.5">
                Venue (optional)
                {checkingAvail && (
                  <span className="w-3 h-3 border-2 border-amber-400/40 border-t-amber-500 rounded-full animate-spin inline-block" />
                )}
              </label>
              <select
                value={venueId}
                onChange={(e) => setVenueId(e.target.value)}
                className={inputCls()}
              >
                <option value="">No venue</option>
                {venues.map((v) => {
                  const vs = venueStatuses.find((s) => s.id === v.id);
                  const suffix = !startDate ? "" : vs?.status === "available" ? " ✓" : vs?.status === "occupied" ? " ✗" : "";
                  return <option key={v.id} value={v.id}>{v.name}{suffix}</option>;
                })}
              </select>

              {/* Inline availability hints when a date is chosen */}
              {startDate && venueStatuses.length > 0 && (
                <div className="mt-2 space-y-1 max-h-36 overflow-y-auto">
                  {venueStatuses.map((vs) => {
                    const venueName = venues.find((v) => v.id === vs.id)?.name ?? `Venue ${vs.id}`;
                    const isSelected = String(vs.id) === venueId;
                    return (
                      <button
                        key={vs.id}
                        type="button"
                        onClick={() => setVenueId(String(vs.id))}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors text-left ${
                          isSelected
                            ? "ring-2 ring-amber-400 bg-amber-50"
                            : "hover:bg-stone-50"
                        } ${
                          vs.status === "available"
                            ? "border border-emerald-200"
                            : "border border-red-200"
                        }`}
                      >
                        {vs.status === "available" ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                        )}
                        <span className="font-medium text-stone-700 flex-1 truncate">{venueName}</span>
                        {vs.status === "occupied" && vs.conflict && (
                          <span className="text-red-600 truncate max-w-[120px]">{vs.conflict.event_name}</span>
                        )}
                        {vs.status === "available" && (
                          <span className="text-emerald-600">Free</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Status</label>
              <select value={status} onChange={(e) => { setStatus(e.target.value); setStatusTouched(true); }} className={inputCls()}>
                <option value="draft">Draft</option>
                <option value="confirmed">Confirmed</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Notes */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-stone-600 mb-1">Notes</label>
              <textarea
                rows={2}
                placeholder="Optional notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={`${inputCls()} resize-none`}
              />
            </div>
          </div>
        </section>

        {/* ── Section 2: Services ───────────────────────────────────────── */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
              Services ({services.length})
            </h3>
            <button
              type="button"
              onClick={() => setServices((p) => [...p, blankService()])}
              className="text-xs px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 font-semibold transition-colors flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add Service
            </button>
          </div>

          {services.length === 0 ? (
            <div className="border-2 border-dashed border-stone-200 rounded-xl py-6 text-center text-stone-400 text-sm">
              No services added. Select a package above or click "Add Service".
            </div>
          ) : (
            <div className="space-y-2">
              {/* Header */}
              <div className="hidden sm:grid grid-cols-12 gap-2 px-2 text-xs font-semibold text-stone-500">
                <span className="col-span-4">Description</span>
                <span className="col-span-2 text-center">Qty</span>
                <span className="col-span-2 text-right">Client Price</span>
                <span className="col-span-2 text-right">Our Cost</span>
                <span className="col-span-2 text-right">Revenue</span>
              </div>
              {services.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-stone-50 rounded-xl p-2">
                  <div className="col-span-12 sm:col-span-4">
                    <input
                      placeholder="Service description"
                      value={s.description}
                      onChange={(e) => updateService(i, "description", e.target.value)}
                      className={inputCls(errors[`svc_desc_${i}`])}
                    />
                    {(s.per_guest || s.vendor_id) && (
                      <p className="mt-0.5 px-1 text-[11px] text-stone-500 truncate">
                        {s.per_guest && <span className="font-medium text-amber-700">Per guest</span>}
                        {s.per_guest && s.vendor_id && " · "}
                        {s.vendor_id && (vendors.find((v) => v.id === s.vendor_id)?.name || s.vendor_name || "Vendor assigned")}
                      </p>
                    )}
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <input
                      type="number" min="1"
                      value={s.quantity}
                      onChange={(e) => updateService(i, "quantity", parseInt(e.target.value) || 1)}
                      className={inputCls()}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <input
                      type="number" min="0" step="0.01" placeholder="0"
                      value={s.unit_price || ""}
                      onChange={(e) => updateService(i, "unit_price", parseFloat(e.target.value) || 0)}
                      className={inputCls(errors[`svc_price_${i}`])}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <input
                      type="number" min="0" step="0.01" placeholder="0"
                      value={s.cost_price || ""}
                      onChange={(e) => updateService(i, "cost_price", parseFloat(e.target.value) || 0)}
                      className={inputCls()}
                    />
                  </div>
                  <div className="col-span-10 sm:col-span-2 text-right text-sm font-semibold text-stone-700">
                    {fmt(s.unit_price * s.quantity)}
                  </div>
                  <div className="col-span-2 sm:col-span-1 flex justify-end">
                    <button type="button" onClick={() => removeService(i)}
                      className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              {/* Services subtotal */}
              <div className="flex justify-between text-sm font-semibold text-stone-700 px-2 pt-1 border-t border-stone-200">
                <span className="text-stone-500">Services Total</span>
                <span>{fmt(totals.svcRevenue)}</span>
              </div>
            </div>
          )}

          {/* Optional add-ons of the chosen package */}
          {selectedPkg && addOns.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-stone-500 mb-2">Add-ons in {selectedPkg.name}</p>
              <div className="flex flex-wrap gap-2">
                {addOns.map((l) => (
                  <button
                    key={l.line_id ?? l.service_id}
                    type="button"
                    onClick={() => addPackageAddOn(l)}
                    className="min-h-[40px] px-3 py-2 rounded-full border border-dashed border-amber-300 bg-amber-50/60 text-amber-800 text-xs sm:text-sm font-medium hover:bg-amber-100 transition-colors"
                  >
                    + {l.name} · {fmt(Number(l.unit_price) || 0)}{unitSuffix(l.unit) ? ` ${unitSuffix(l.unit)}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ── Section 3: Vendors ────────────────────────────────────────── */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
              Vendor Assignments ({eventVendors.length})
            </h3>
            <button
              type="button"
              onClick={() => setEventVendors((p) => [...p, blankVendor()])}
              className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 font-semibold transition-colors flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add Vendor
            </button>
          </div>

          {eventVendors.length === 0 ? (
            <div className="border-2 border-dashed border-stone-200 rounded-xl py-6 text-center text-stone-400 text-sm">
              No vendors assigned. Click "Add Vendor" to add vendor costs.
            </div>
          ) : (
            <div className="space-y-3">
              {eventVendors.map((v, i) => (
                <div key={i} className="bg-stone-50 border border-stone-200 rounded-xl p-3 space-y-2">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    {/* Vendor select */}
                    <div className="col-span-12 sm:col-span-4">
                      <label className="block text-xs text-stone-500 mb-0.5">Vendor *</label>
                      <select
                        value={v.vendor_id ?? ""}
                        onChange={(e) => updateVendor(i, "vendor_id", parseInt(e.target.value) || null)}
                        className={inputCls(errors[`vnd_id_${i}`])}
                      >
                        <option value="">Select vendor</option>
                        {vendors.map((vnd) => <option key={vnd.id} value={vnd.id}>{vnd.name}</option>)}
                      </select>
                    </div>
                    {/* Description */}
                    <div className="col-span-12 sm:col-span-8">
                      <label className="block text-xs text-stone-500 mb-0.5">Description *</label>
                      <input
                        placeholder="e.g. Catering for 200 pax"
                        value={v.description}
                        onChange={(e) => updateVendor(i, "description", e.target.value)}
                        className={inputCls(errors[`vnd_desc_${i}`])}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-2 items-end">
                    {/* Qty */}
                    <div className="col-span-3 sm:col-span-2">
                      <label className="block text-xs text-stone-500 mb-0.5">Qty</label>
                      <input
                        type="number" min="1"
                        value={v.quantity}
                        onChange={(e) => updateVendor(i, "quantity", parseInt(e.target.value) || 1)}
                        className={inputCls()}
                      />
                    </div>
                    {/* Client billing price */}
                    <div className="col-span-4 sm:col-span-2">
                      <label className="block text-xs text-stone-500 mb-0.5">Client Price ₹</label>
                      <input
                        type="number" min="0" step="0.01" placeholder="0"
                        value={v.unit_price || ""}
                        onChange={(e) => updateVendor(i, "unit_price", parseFloat(e.target.value) || 0)}
                        className={inputCls(errors[`vnd_price_${i}`])}
                      />
                    </div>
                    {/* Vendor cost */}
                    <div className="col-span-4 sm:col-span-2">
                      <label className="block text-xs text-stone-500 mb-0.5">Vendor Cost ₹</label>
                      <input
                        type="number" min="0" step="0.01" placeholder="0"
                        value={v.cost_price || ""}
                        onChange={(e) => updateVendor(i, "cost_price", parseFloat(e.target.value) || 0)}
                        className={inputCls()}
                      />
                    </div>
                    {/* Commission % */}
                    <div className="col-span-4 sm:col-span-2">
                      <label className="block text-xs text-stone-500 mb-0.5">Commission</label>
                      <input
                        type="number" min="0" step="0.01" placeholder="0"
                        value={v.commission_percent || ""}
                        onChange={(e) => updateVendor(i, "commission_percent", parseFloat(e.target.value) || 0)}
                        className={inputCls()}
                      />
                    </div>
                    {/* Commission type */}
                    <div className="col-span-4 sm:col-span-2">
                      <label className="block text-xs text-stone-500 mb-0.5">Type</label>
                      <select
                        value={v.commission_type}
                        onChange={(e) => updateVendor(i, "commission_type", e.target.value)}
                        className={inputCls()}
                      >
                        <option value="percentage">%</option>
                        <option value="fixed">Fixed ₹</option>
                      </select>
                    </div>
                    {/* Revenue display + delete */}
                    <div className="col-span-8 sm:col-span-1 flex items-end justify-between sm:justify-end gap-2">
                      <span className="text-sm font-semibold text-stone-700 pb-2">{fmt(v.unit_price * v.quantity)}</span>
                      <button type="button" onClick={() => removeVendor(i)}
                        className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50 transition-colors mb-1">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Notes */}
                  <input
                    placeholder="Notes (optional)"
                    value={v.notes}
                    onChange={(e) => updateVendor(i, "notes", e.target.value)}
                    className={`${inputCls()} text-xs`}
                  />
                </div>
              ))}
              {/* Vendors subtotal */}
              <div className="flex justify-between text-sm font-semibold text-stone-700 px-2 pt-1 border-t border-stone-200">
                <span className="text-stone-500">Vendors Total</span>
                <span>{fmt(totals.vndRevenue)}</span>
              </div>
            </div>
          )}
        </section>

        {/* ── Section 4: Financial Summary & Payment ───────────────────── */}
        <section>
          <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Payment & Summary</h3>

          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-center">
              <p className="text-xs text-stone-500 mb-1">Services</p>
              <p className="text-sm font-bold text-stone-800">{fmt(totals.svcRevenue)}</p>
            </div>
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-center">
              <p className="text-xs text-stone-500 mb-1">Vendors</p>
              <p className="text-sm font-bold text-stone-800">{fmt(totals.vndRevenue)}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
              <p className="text-xs text-amber-700 mb-1">Total</p>
              <p className="text-sm font-bold text-amber-800">{fmt(totals.total)}</p>
              {totals.discount > 0 && (
                <p className="text-[10px] text-amber-700 mt-0.5">after {fmt(totals.discount)} off</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Discount */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-stone-600 mb-1">Discount</label>
              <div className="flex gap-2">
                <select
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value as "fixed" | "percentage")}
                  className={`${inputCls()} w-24 flex-shrink-0`}
                  aria-label="Discount type"
                >
                  <option value="fixed">₹</option>
                  <option value="percentage">%</option>
                </select>
                <input
                  type="number" min="0" step="0.01" placeholder="0"
                  max={discountType === "percentage" ? 100 : undefined}
                  value={discountValue}
                  onChange={(e) => { setDiscountValue(e.target.value); setErrors((p) => ({ ...p, discount: "" })); }}
                  className={inputCls(errors.discount)}
                />
              </div>
              {errors.discount && <p className="text-xs text-rose-500 mt-1">{errors.discount}</p>}
              {discountIsFromPackage && pkgDiscount && (
                <p className="text-xs text-amber-600 mt-1">Package discount from {pkgDiscount.from}</p>
              )}
            </div>

            {/* Advance payment */}
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Amount Paid (₹)</label>
              <input
                type="number" min="0" step="0.01" placeholder="0"
                value={advancePayment}
                onChange={(e) => setAdvancePayment(e.target.value)}
                className={inputCls()}
              />
            </div>

            {/* Payment method */}
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Payment Method</label>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={inputCls()}>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="card">Card</option>
              </select>
            </div>

            {/* Payment date */}
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Payment Date</label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className={inputCls()}
              />
            </div>

            {/* Balance indicator */}
            <div className={`flex items-center justify-between rounded-xl px-4 py-3 ${totals.balance <= 0 ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
              <span className={`text-sm font-semibold ${totals.balance <= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {totals.balance <= 0 ? "Fully Paid" : "Balance Due"}
              </span>
              <span className={`text-lg font-bold ${totals.balance <= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {fmt(Math.abs(totals.balance))}
              </span>
            </div>
          </div>
        </section>

      </form>
    </Modal>
  );
}
